import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { getChartVisualization, generateChartsForNLPQuery, executeQuery } from '../services/api';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BarChart3, TrendingUp, PieChart as PieChartIcon, Table, Check, Sparkles, RefreshCw, AlertCircle } from 'lucide-react';

// Chart type icons mapping - only bar, line, pie, and table are supported
const chartIcons = {
    bar: BarChart3,
    line: TrendingUp,
    pie: PieChartIcon,
    table: Table
};

// Custom color palette for charts
const CHART_COLORS = [
    '#75B7BA',
    '#848FA1',
    '#8CDAD6',
    '#90C38A',
    '#91BBE5',
    '#95D3E2',
    '#A6D976',
    '#B2E46B',
    '#BDBDBD',
    '#D6B0A7',
    '#DCCC9F',
    '#E3ADC5',
    '#EA98B5',
    '#F0B666',
    '#F0DDBC',
    '#F4B69D',
    '#F57F72',
    '#F5CEE1',
    '#F9C4BA',
    '#FB8C3F',
    '#FBE225',
    '#FF8965',
    '#FFDF5D'
];

// Colors for pie charts
const PIE_COLORS = CHART_COLORS;

export const Charts = () => {
    const { t } = useTranslation(['page-charts', 'common']);
    const [searchParams] = useSearchParams();
    const { selectedDatabase, databases, setSelectedDatabase } = useDatabase();
    const { showToast } = useToast();

    const [loading, setLoading] = useState(false);
    const [executing, setExecuting] = useState(false);
    const [generatingCharts, setGeneratingCharts] = useState(false);
    const [visualizationData, setVisualizationData] = useState(null);
    const [chartRecommendations, setChartRecommendations] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [selectedChartType, setSelectedChartType] = useState(null);
    const [selectedChartConfig, setSelectedChartConfig] = useState(null);
    const [chartDataLoaded, setChartDataLoaded] = useState(false);
    const [queryParameters, setQueryParameters] = useState([]);
    const [parameterValues, setParameterValues] = useState({});

    const queryId = searchParams.get('query');
    const dbId = searchParams.get('db') || selectedDatabase?.id;

    // Filter databases to only show those with schema analysis completed
    const analyzedDatabases = databases.filter(db =>
        db.status && db.status.toLowerCase() === 'analyzed'
    );

    const handleDatabaseChange = (e) => {
        const dbId = e.target.value;
        const db = databases.find(db => db.id === parseInt(dbId));
        if (db) {
            setSelectedDatabase(db);
            // No toast notification for database switch
        }
    };

    useEffect(() => {
        if (queryId && dbId) {
            fetchChartVisualization();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryId, dbId]);

    const fetchChartVisualization = async () => {
        if (!queryId || !dbId) return;

        setLoading(true);
        try {
            // Get chart visualization data
            const res = await getChartVisualization(dbId, queryId);
            const data = res.data?.data;

            setVisualizationData({
                query_id: data.query_id,
                query_name: data.query_name,
                query_description: data.query_description,
                query_sql: data.query_sql
            });

            if (data.chart_recommendations && data.chart_recommendations.length > 0) {
                setChartRecommendations(data.chart_recommendations);

                // Set the first (highest priority) chart as default
                const primaryChart = data.chart_recommendations[0];
                setSelectedChartType(primaryChart.type);
                setSelectedChartConfig(primaryChart.config);
            } else {
                // No chart recommendations exist, silently note - no toast
            }
            
            // Extract and set query parameters
            extractQueryParameters(data.query_sql);
        } catch (error) {
            console.error('Failed to fetch chart visualization:', error);
            showToast(error.response?.data?.detail || t('page-charts:failedFetchVisualization'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const extractQueryParameters = (querySql) => {
        if (!querySql) return;
        
        // Extract parameters from the query SQL (look for {{param_name}} patterns)
        const paramMatches = querySql.match(/\{\{(\w+)\}\}/g);
        
        if (paramMatches) {
            const params = paramMatches.map(match => {
                const paramName = match.replace('{{', '').replace('}}', '');
                return {
                    name: paramName,
                    type: 'string',
                    required: true
                };
            });
            // Remove duplicates
            const uniqueParams = params.filter((v, i, a) => a.findIndex(t => t.name === v.name) === i);
            setQueryParameters(uniqueParams);
            
            // Initialize with default values
            const initialValues = {};
            uniqueParams.forEach(p => {
                if (p.name === 'organization_id') initialValues[p.name] = '1';
                if (p.name === 'start_date') initialValues[p.name] = '2025-01-01';
                if (p.name === 'end_date') initialValues[p.name] = '2026-12-31';
                if (p.name === 'top_n') initialValues[p.name] = '5';
                if (p.name === 'min_invoice_count') initialValues[p.name] = '1';
                if (p.name === 'payment_status') initialValues[p.name] = 'unpaid';
            });
            setParameterValues(initialValues);
        } else {
            setQueryParameters([]);
        }
    };

    const handleGenerateCharts = async () => {
        if (!queryId || !dbId) return;

        setGeneratingCharts(true);
        try {
            // Generate charts for NLP query (works for both NLP and database queries)
            const res = await generateChartsForNLPQuery(dbId, queryId);
            const data = res.data?.data;

            if (data.chart_recommendations && data.chart_recommendations.length > 0) {
                setChartRecommendations(data.chart_recommendations);

                // Set the first (highest priority) chart as default
                const primaryChart = data.chart_recommendations.find(c => c.priority === 1) || data.chart_recommendations[0];
                setSelectedChartType(primaryChart.type);
                setSelectedChartConfig(primaryChart.config);

                // No toast notification for chart generation
            } else {
                // No chart recommendations could be generated - no toast
            }
        } catch (error) {
            console.error('Error generating chart recommendations:', error);
            showToast(error.response?.data?.detail || t('page-charts:failedGenerateRecommendations'), 'error');
        } finally {
            setGeneratingCharts(false);
        }
    };

    const handleSelectChart = (chart) => {
        setSelectedChartType(chart.type);
        setSelectedChartConfig(chart.config);
        // No toast notification for chart type switch
    };

    const handleExecuteQuery = async () => {
        if (!queryId || !dbId) return;

        // Execute with current parameter values
        await executeQueryWithParams(parameterValues);
    };

    const executeQueryWithParams = async (params) => {
        setExecuting(true);
        try {
            const response = await executeQuery({
                database_id: dbId,
                query_id: queryId,
                parameters: params
            });

            // Backend returns: { status, message, data: { rows, columns, row_count, ... } }
            // Extract rows from the response
            const responseData = response.data?.data;
            const data = responseData?.rows || responseData?.data || [];

            setChartData(data);
            setChartDataLoaded(true);
            // No toast notification for query execution
        } catch (error) {
            console.error('Error executing query:', error);
            showToast(error.response?.data?.message || error.message || t('page-charts:failedExecuteQuery'), 'error');
            setChartData([]);
            setChartDataLoaded(false);
        } finally {
            setExecuting(false);
        }
    };

    const handleParamChange = (paramName, value) => {
        setParameterValues(prev => ({
            ...prev,
            [paramName]: value
        }));
    };

    const transformDataForChart = (data, chartConfig, tooltipFields) => {
        if (!data || data.length === 0) return [];

        // For table view, return data as-is
        if (selectedChartType === 'table') {
            return data;
        }

        // Extract transformation rules from config
        const transformRules = chartConfig?.transform_rules;
        const categoryField = transformRules?.category_field;
        const valueField = transformRules?.value_field;

        if (!categoryField || !valueField) {
            // Fallback to first two columns
            const fallbackKeys = Object.keys(data[0]);
            if (fallbackKeys.length >= 2) {
                return transformFallback(data, fallbackKeys[0], fallbackKeys[1]);
            }
            return data;
        }

        // Helper to convert value to number (handles Decimal, strings, etc.)
        const toNumber = (val) => {
            if (typeof val === 'number') return val;
            if (val === null || val === undefined) return 0;
            // Handle Decimal objects or strings
            const num = parseFloat(String(val));
            return isNaN(num) ? 0 : num;
        };

        // Helper to format category (handle dates, nulls, etc.)
        const formatCategory = (val, categoryType, dateFormat) => {
            if (val === null || val === undefined) return 'N/A';
            
            // If it's already a string, return it
            if (typeof val === 'string') {
                // Clean up newlines in strings for display
                return val.replace(/\n/g, ' - ');
            }
            
            // If it's a number and category type is not time, return as string
            if (typeof val === 'number' && categoryType !== 'time') {
                return String(val);
            }
            
            // Handle Date objects
            if (val instanceof Date) {
                if (dateFormat) {
                    // Simple date formatting based on format string
                    const year = val.getFullYear();
                    const month = String(val.getMonth() + 1).padStart(2, '0');
                    const monthName = val.toLocaleString('default', { month: 'short' });
                    const day = String(val.getDate()).padStart(2, '0');
                    
                    if (dateFormat === 'YYYY-MM') return `${year}-${month}`;
                    if (dateFormat === 'MMM YYYY') return `${monthName} ${year}`;
                    if (dateFormat === 'YYYY-MM-DD') return `${year}-${month}-${day}`;
                }
                return val.toLocaleDateString();
            }
            
            // Handle ISO date strings
            if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}/)) {
                const date = new Date(val);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString();
                }
            }
            
            return String(val);
        };

        // For pie charts
        if (selectedChartType === 'pie') {
            return data.map(item => ({
                name: formatCategory(item[categoryField], transformRules.category_type, transformRules.date_format),
                value: toNumber(item[valueField]),
                rawCategory: item[categoryField], // Keep raw value for tooltips
                rawData: item // Keep full raw data for tooltip access
            }));
        }

        // For doughnut charts - same transformation as pie charts
        if (selectedChartType === 'doughnut') {
            return data.map(item => ({
                name: formatCategory(item[categoryField], transformRules.category_type, transformRules.date_format),
                value: toNumber(item[valueField]),
                rawCategory: item[categoryField],
                rawData: item
            }));
        }

        // For bar and line charts
        if (selectedChartType === 'bar' || selectedChartType === 'line') {
            return data.map(item => ({
                category: formatCategory(item[categoryField], transformRules.category_type, transformRules.date_format),
                value: toNumber(item[valueField]),
                rawCategory: item[categoryField], // Keep raw value for tooltips
                rawData: item // Keep full raw data for tooltip access
            }));
        }

        return data;
    };

    const transformFallback = (data, xKey, yKey) => {
        if (selectedChartType === 'pie' || selectedChartType === 'doughnut') {
            return data.map(item => ({
                name: String(item[xKey]),
                value: typeof item[yKey] === 'number' ? item[yKey] : parseFloat(item[yKey]) || 0
            }));
        }

        if (selectedChartType === 'bar' || selectedChartType === 'line') {
            return data.map(item => ({
                category: String(item[xKey]),
                value: typeof item[yKey] === 'number' ? item[yKey] : parseFloat(item[yKey]) || 0
            }));
        }

        return data;
    };

    const renderChart = () => {
        const transformRules = selectedChartConfig?.transform_rules || {};
        const categoryLabel = transformRules.category_label || t('page-charts:categoryLabel');
        const valueLabel = transformRules.value_label || t('page-charts:valueLabel');
        const tooltipFields = selectedChartConfig?.tooltip_fields || [];

        // Custom tooltip component to show additional fields
        const CustomTooltip = ({ active, payload, label }) => {
            if (active && payload && payload.length) {
                const data = payload[0].payload.rawData || {};
                return (
                    <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-4 text-sm">
                        <p className="font-semibold text-gray-900 mb-2">{label}</p>
                        {tooltipFields.length > 0 ? (
                            <div className="space-y-1">
                                {tooltipFields.map((field, idx) => {
                                    const value = data[field];
                                    // Format Decimal or date objects
                                    const displayValue = value instanceof Object
                                        ? (typeof value.toString === 'function' ? value.toString() : JSON.stringify(value))
                                        : String(value ?? 'N/A');
                                    return (
                                        <p key={idx} className="text-gray-700">
                                            <span className="text-gray-500 capitalize">{field.replace(/_/g, ' ')}:</span>{' '}
                                            {displayValue}
                                        </p>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-gray-700">{valueLabel}: {payload[0].value}</p>
                        )}
                    </div>
                );
            }
            return null;
        };

        // Handle case when query was executed but returned no results
        if (!chartDataLoaded) {
            return (
                <div className="flex items-center justify-center h-96 text-gray-500">
                    <p>{t('page-charts:executeQueryToDisplay')}</p>
                </div>
            );
        }

        if (chartData.length === 0) {
            // Query executed but no results returned
            if (selectedChartType === 'table') {
                // Show empty table with column headers from expected columns or config
                const expectedColumns = visualizationData?.expected_columns || [];
                const columns = expectedColumns.length > 0 ? expectedColumns :
                    (transformRules.category_field ? [transformRules.category_field, transformRules.value_field] : [t('page-charts:noColumnsAvailable')]);

                return (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                            <AlertCircle className="w-5 h-5" />
                            <p className="text-sm font-medium">{t('page-charts:noResultsForQuery')}</p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {columns.map((key, idx) => (
                                            <th key={idx} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {typeof key === 'string' ? key.replace(/_/g, ' ') : t('page-charts:columnFallback', { num: idx + 1 })}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-8 text-center text-sm text-gray-400">
                                            {t('page-charts:noDataAvailable')}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            }

            // For bar, line, pie charts - show skeleton with axes
            return (
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                        <AlertCircle className="w-5 h-5" />
                        <p className="text-sm font-medium">{t('page-charts:noResultsForQuery')}</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                        <ResponsiveContainer width="100%" height={400}>
                            {selectedChartType === 'pie' || selectedChartType === 'doughnut' ? (
                                <PieChart>
                                    <Tooltip />
                                    <Legend />
                                    <text x="50%" y="50%" textAnchor="middle" fill="#9ca3af" fontSize="14">
                                        {t('page-charts:noDataToDisplay')}
                                    </text>
                                </PieChart>
                            ) : selectedChartType === 'bar' ? (
                                <BarChart data={[]}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis
                                        dataKey="category"
                                        label={{ value: categoryLabel, position: 'insideBottom', offset: -5 }}
                                        tick={false}
                                    />
                                    <YAxis
                                        label={{ value: valueLabel, angle: -90, position: 'insideLeft' }}
                                        tick={false}
                                    />
                                    <Tooltip />
                                    <Legend />
                                </BarChart>
                            ) : (
                                <LineChart data={[]}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis
                                        dataKey="category"
                                        label={{ value: categoryLabel, position: 'insideBottom', offset: -5 }}
                                        tick={false}
                                    />
                                    <YAxis
                                        label={{ value: valueLabel, angle: -90, position: 'insideLeft' }}
                                        tick={false}
                                    />
                                    <Tooltip />
                                    <Legend />
                                </LineChart>
                            )}
                        </ResponsiveContainer>
                    </div>
                </div>
            );
        }

        const transformedData = transformDataForChart(chartData, selectedChartConfig, tooltipFields);

        switch (selectedChartType) {
            case 'bar':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={transformedData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="category" label={{ value: categoryLabel, position: 'insideBottom', offset: -5 }} />
                            <YAxis label={{ value: valueLabel, angle: -90, position: 'insideLeft' }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="value" fill={CHART_COLORS[0]} name={valueLabel} />
                        </BarChart>
                    </ResponsiveContainer>
                );

            case 'line':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={transformedData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="category" label={{ value: categoryLabel, position: 'insideBottom', offset: -5 }} />
                            <YAxis label={{ value: valueLabel, angle: -90, position: 'insideLeft' }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} name={valueLabel} />
                        </LineChart>
                    </ResponsiveContainer>
                );

            case 'pie':
            case 'doughnut':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                            <Pie
                                data={transformedData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={selectedChartType === 'doughnut' ? 120 : 150}
                                innerRadius={selectedChartType === 'doughnut' ? 60 : 0}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {transformedData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                );

            case 'table':
            default:
                const keys = Object.keys(chartData[0] || {});
                return (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {keys.map(key => (
                                        <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {key}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {chartData.map((item, index) => (
                                    <tr key={index}>
                                        {keys.map(key => (
                                            <td key={key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {typeof item[key] === 'object' ? JSON.stringify(item[key]) : String(item[key])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
        }
    };

    if (!queryId) {
        return (
            <div className="page-shell">
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('page-charts:noQuerySelected')}</h3>
                        <p className="text-gray-600">{t('page-charts:selectQueryFromGenerator')}</p>
                    </div>
            </div>
        );
    }

    return (
        <div className="page-shell">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{t('page-charts:chartVisualization')}</h1>
                        <p className="text-sm text-gray-500 mt-1">{t('page-charts:visualizeSubtitle')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <select
                            value={dbId || ''}
                            onChange={handleDatabaseChange}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                        >
                            <option value="">{t('page-charts:selectDatabase')}</option>
                            {analyzedDatabases.map((db) => (
                                <option key={db.id} value={db.id}>{db.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : visualizationData ? (
                    <div className="space-y-6">
                        {/* Query Info Card */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('page-charts:queryInformation')}</h3>
                            <div className="space-y-3">
                                <div>
                                    <span className="text-sm font-medium text-gray-700">{t('page-charts:nameLabel')}</span>
                                    <span className="ml-2 text-gray-900">{visualizationData.query_name}</span>
                                </div>
                                {visualizationData.query_description && (
                                    <div>
                                        <span className="text-sm font-medium text-gray-700">{t('page-charts:descriptionLabel')}</span>
                                        <p className="ml-2 text-gray-900 text-sm mt-1">{visualizationData.query_description}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Chart Generation Button */}
                        {chartRecommendations.length === 0 && (
                            <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                                <Sparkles className="w-12 h-12 text-primary-400 mx-auto mb-3" />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('page-charts:noChartRecommendations')}</h3>
                                <p className="text-gray-600 mb-4">{t('page-charts:generateChartsSubtitle')}</p>
                                <button
                                    onClick={handleGenerateCharts}
                                    disabled={generatingCharts}
                                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                                >
                                    <RefreshCw className={`w-4 h-4 ${generatingCharts ? 'animate-spin' : ''}`} />
                                    {generatingCharts ? t('page-charts:generating') : t('page-charts:generateChartRecommendations')}
                                </button>
                            </div>
                        )}

                        {/* Chart Type Selection */}
                        {chartRecommendations.length > 0 && (
                            <div className="bg-white rounded-lg border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    {t('page-charts:chartRecommendation')}
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {/* Chart Recommendations */}
                                    {chartRecommendations.map((chart) => {
                                        const Icon = chartIcons[chart.type] || BarChart3;
                                        const isSelected = selectedChartType === chart.type;

                                        return (
                                            <button
                                                key={chart.id || chart.type}
                                                onClick={() => handleSelectChart(chart)}
                                                className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                                                    isSelected
                                                        ? 'border-green-500 bg-green-50 shadow-md'
                                                        : 'border-yellow-400 bg-yellow-50 hover:border-primary-300 hover:shadow'
                                                }`}
                                            >
                                                {isSelected && (
                                                    <div className="absolute top-2 right-2">
                                                        <div className="bg-green-500 text-white rounded-full p-1">
                                                            <Check className="w-3 h-3" />
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="flex items-start gap-3">
                                                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                        isSelected ? 'bg-green-200' : 'bg-yellow-200'
                                                    }`}>
                                                        <Icon className={`w-6 h-6 ${
                                                            isSelected ? 'text-green-700' : 'text-yellow-700'
                                                        }`} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h4 className="font-semibold text-gray-900 capitalize">{t('page-charts:typeChart', { type: chart.type })}</h4>
                                                            {chart.priority === 1 && (
                                                                <span className="px-2 py-0.5 bg-primary-200 text-primary-800 text-xs font-medium rounded flex items-center gap-1">
                                                                    <Sparkles className="w-3 h-3" />
                                                                    {t('page-charts:recommended')}
                                                                </span>
                                                            )}
                                                            {isSelected && (
                                                                <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs font-medium rounded">
                                                                    {t('page-charts:selected')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{chart.reason}</p>
                                                        {chart.config?.transform_rules?.category_field && chart.config?.transform_rules?.value_field && (
                                                            <p className="text-xs text-gray-500 mt-2">
                                                                X: {chart.config.transform_rules.category_label || chart.config.transform_rules.category_field} | Y: {chart.config.transform_rules.value_label || chart.config.transform_rules.value_field}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}

                                    {/* Table View Option - Only show if there are non-table chart recommendations */}
                                    {(() => {
                                        const hasNonTableCharts = chartRecommendations.some(chart => chart.type !== 'table');
                                        
                                        // Only show the Table View option if there are other chart types besides table
                                        if (!hasNonTableCharts) {
                                            return null;
                                        }
                                        
                                        const isSelected = selectedChartType === 'table';
                                        const Icon = chartIcons.table;

                                        return (
                                            <button
                                                onClick={() => handleSelectChart({ type: 'table', config: {} })}
                                                className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                                                    isSelected
                                                        ? 'border-green-500 bg-green-50 shadow-md'
                                                        : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                                                }`}
                                            >
                                                {isSelected && (
                                                    <div className="absolute top-2 right-2">
                                                        <div className="bg-green-500 text-white rounded-full p-1">
                                                            <Check className="w-3 h-3" />
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="flex items-start gap-3">
                                                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                        isSelected ? 'bg-green-200' : 'bg-gray-100'
                                                    }`}>
                                                        <Icon className={`w-6 h-6 ${
                                                            isSelected ? 'text-green-700' : 'text-gray-700'
                                                        }`} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h4 className="font-semibold text-gray-900">{t('page-charts:tableView')}</h4>
                                                            {isSelected && (
                                                                <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs font-medium rounded">
                                                                    {t('page-charts:selected')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-600 mt-1">{t('page-charts:viewRawDataTabular')}</p>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Query Parameters */}
                        {queryParameters.length > 0 && (
                            <div className="bg-white rounded-lg border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('page-charts:queryParameters')}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {queryParameters.map((param) => {
                                        // Determine input type - only use date picker for actual date types
                                        const inputType = param.type === 'date' || param.type === 'datetime' ? 'date' : 
                                                         param.type === 'integer' || param.type === 'float' ? 'number' : 'text';
                                        
                                        return (
                                            <div key={param.name}>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {param.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                </label>
                                                <input
                                                    type={inputType}
                                                    value={parameterValues[param.name] || ''}
                                                    onChange={(e) => handleParamChange(param.name, e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                                    placeholder={t('page-charts:enterParam', { param: param.name })}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Execute Query Button */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <button
                                onClick={handleExecuteQuery}
                                disabled={executing}
                                className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {executing ? t('page-charts:executingQuery') : t('page-charts:executeQuery')}
                            </button>
                        </div>

                        {/* Chart Display */}
                        {chartDataLoaded && (
                            <div className="bg-white rounded-lg border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    {selectedChartType === 'table' ? t('page-charts:dataTable') : t('page-charts:typeChart', { type: selectedChartType?.charAt(0).toUpperCase() + selectedChartType.slice(1) })}
                                </h3>
                                {renderChart()}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <p className="text-gray-600">{t('common:loading')}</p>
                    </div>
                )}
        </div>
    );
};
